import type {
  UseFormSetValue,
  UseFormWatch,
  FieldErrors,
} from "react-hook-form";
import { MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { EditableGeocodedLocation, SectionCard } from "@/components/common";
import type { Activity } from "@/types";
import type { AddressValidationState } from "@/types";
import type { ActivityDetailFormValues } from "../activityDetailForm";

interface LocationSectionProps {
  activity: Activity;
  setValue: UseFormSetValue<ActivityDetailFormValues>;
  watch: UseFormWatch<ActivityDetailFormValues>;
  errors: FieldErrors<ActivityDetailFormValues>;
  modified: boolean;
  onValidationChange?: (state: AddressValidationState) => void;
}

export function LocationSection({
  activity,
  setValue,
  watch,
  errors,
  modified,
  onValidationChange,
}: LocationSectionProps) {
  const { t } = useTranslation(["activities", "common"]);

  const address = watch("address");
  const coordinates = watch("coordinates");

  const addressLabels = {
    search: t("common:addressSearch.label"),
    searchPlaceholder: t("common:addressSearch.placeholder"),
  };

  return (
    <SectionCard
      title={t("activities:detailForm.locationTitle")}
      icon={MapPin}
      modified={modified}
      className="mb-6"
    >
      <EditableGeocodedLocation
        value={{ address, coordinates }}
        onChange={(next) => {
          setValue("address", next.address, { shouldDirty: true });
          setValue("coordinates", next.coordinates, { shouldDirty: true });
        }}
        labels={addressLabels}
        syncKey={`${activity.id}-${activity.updated_at}`}
        error={errors.address?.message}
        onValidationChange={onValidationChange}
      />
    </SectionCard>
  );
}
